import { globalState } from '../state.js';
import { elements } from '../domElements.js';
import { sendControlMessageToServer } from '../websocketService.js';
import * as C from '../constants.js';

// 防止触摸事件和鼠标事件双重触发的标志
let touchHandled = false;

/**
 * 将屏幕坐标转换为设备坐标
 * 修复了触摸结束事件中无法获取坐标的问题
 */
function getScaledCoordinates(event) {
    let targetElement;
    // 根据解码器类型选择目标元素（画布或视频元素）
    if (globalState.decoderType === C.DECODER_TYPES.BROADWAY) {
        targetElement = globalState.broadwayPlayer ? globalState.broadwayPlayer.canvas : null;
        if (!targetElement) {
            targetElement = elements.broadwayCanvas;
        }
    } else if (globalState.decoderType === C.DECODER_TYPES.WEBCODECS) {
        targetElement = elements.webcodecCanvas;
    } else { // MSE or default
        targetElement = elements.videoElement;
    }

    // 检查元素和设备尺寸是否有效
    if (!targetElement || !targetElement.clientWidth || !targetElement.clientHeight || !globalState.deviceWidth || !globalState.deviceHeight) {
        return null;
    }

    const rect = targetElement.getBoundingClientRect();
    let { clientWidth, clientHeight } = targetElement;
    let touchX, touchY;

    // 区分处理触摸事件和鼠标事件
    if (event.type.startsWith('touch')) {
        // 触摸开始和移动事件使用 touches 属性
        if (event.touches && event.touches.length > 0) {
            touchX = event.touches[0].clientX - rect.left;
            touchY = event.touches[0].clientY - rect.top;
        } 
        // 触摸结束和取消事件使用 changedTouches 属性
        else if (event.changedTouches && event.changedTouches.length > 0) {
            touchX = event.changedTouches[0].clientX - rect.left;
            touchY = event.changedTouches[0].clientY - rect.top;
        } else {
            return null;
        }
    } 
    // 处理鼠标事件
    else {
        touchX = event.clientX - rect.left;
        touchY = event.clientY - rect.top;
    }

    // 根据元素和设备的宽高比调整坐标，处理黑边情况
    const videoRatio = globalState.deviceWidth / globalState.deviceHeight;
    const elementRatio = clientWidth / clientHeight;

    if (elementRatio > videoRatio) {
        const realWidth = clientHeight * videoRatio;
        const barsWidth = (clientWidth - realWidth) / 2;
        if (touchX < barsWidth || touchX > barsWidth + realWidth) return null;
        touchX -= barsWidth;
        clientWidth = realWidth;
    } else if (elementRatio < videoRatio) {
        const realHeight = clientWidth / videoRatio;
        const barsHeight = (clientHeight - realHeight) / 2;
        if (touchY < barsHeight || touchY > barsHeight + realHeight) return null;
        touchY -= barsHeight;
        clientHeight = realHeight;
    }

    // 计算最终的设备坐标并限制在有效范围内
    let deviceX = Math.round((touchX * globalState.deviceWidth) / clientWidth);
    let deviceY = Math.round((touchY * globalState.deviceHeight) / clientHeight);
    deviceX = Math.max(0, Math.min(globalState.deviceWidth, deviceX));
    deviceY = Math.max(0, Math.min(globalState.deviceHeight, deviceY));
    return { x: deviceX, y: deviceY };
}

/**
 * 发送鼠标/触摸事件到服务端
 */
function sendMouseEvent(action, buttons, x, y) {
    if (!globalState.deviceWidth || !globalState.deviceHeight || !globalState.controlEnabledAtStart) return;
    const buffer = new ArrayBuffer(32);
    const dataView = new DataView(buffer);
    dataView.setUint8(0, C.CONTROL_MSG_TYPE_INJECT_TOUCH_EVENT);
    dataView.setUint8(1, action);
    dataView.setBigInt64(2, C.POINTER_ID_MOUSE, false);
    dataView.setInt32(10, x, false);
    dataView.setInt32(14, y, false);
    dataView.setUint16(18, globalState.deviceWidth, false);
    dataView.setUint16(20, globalState.deviceHeight, false);
    dataView.setUint16(22, 0xFFFF, false);
    dataView.setUint32(24, 0, false);
    dataView.setUint32(28, buttons, false);
    sendControlMessageToServer(buffer);
}

/**
 * 发送返回键控制消息
 */
function sendBackButtonControlInternal() {
    if (!globalState.isRunning || !globalState.controlEnabledAtStart) return;
    const bufferDown = new ArrayBuffer(2);
    const dataViewDown = new DataView(bufferDown);
    dataViewDown.setUint8(0, C.CONTROL_MSG_TYPE_BACK_OR_SCREEN_ON_CLIENT);
    dataViewDown.setUint8(1, 0);
    sendControlMessageToServer(bufferDown);

    const bufferUp = new ArrayBuffer(2);
    const dataViewUp = new DataView(bufferUp);
    dataViewUp.setUint8(0, C.CONTROL_MSG_TYPE_BACK_OR_SCREEN_ON_CLIENT);
    dataViewUp.setUint8(1, 1);
    sendControlMessageToServer(bufferUp);
}

/**
 * 处理触摸开始事件
 */
function handleTouchStart(event) {
    console.log('Touch start detected');
    // 标记触摸事件已处理，防止鼠标事件重复触发
    touchHandled = true;
    
    if (!globalState.isRunning || !globalState.controlEnabledAtStart || !globalState.deviceWidth || !globalState.deviceHeight) return;

    let activeRenderingElement;
    // 根据解码器类型选择活动渲染元素
    if (globalState.decoderType === C.DECODER_TYPES.BROADWAY) {
        activeRenderingElement = globalState.broadwayPlayer ? globalState.broadwayPlayer.canvas : elements.broadwayCanvas;
    } else if (globalState.decoderType === C.DECODER_TYPES.WEBCODECS) {
        activeRenderingElement = elements.webcodecCanvas;
    } else { // MSE or default
        activeRenderingElement = elements.videoElement;
    }

    // 检查事件是否发生在渲染元素内
    if (!activeRenderingElement || !event.target || (event.target !== activeRenderingElement && !activeRenderingElement.contains(event.target))) {
        if (event.target !== elements.streamArea) return;
    }

    event.preventDefault();
    globalState.isMouseDown = true;

    // 设置主按钮标志并发送触摸按下事件
    let buttonFlag = C.AMOTION_EVENT_BUTTON_PRIMARY;
    globalState.currentMouseButtons |= buttonFlag;
    const coords = getScaledCoordinates(event);
    if (coords) {
        globalState.lastMousePosition = coords;
        sendMouseEvent(C.AMOTION_EVENT_ACTION_DOWN, globalState.currentMouseButtons, coords.x, coords.y);
    }
}

/**
 * 处理触摸移动事件
 */
function handleTouchMove(event) {
    if (!globalState.isRunning || !globalState.controlEnabledAtStart || !globalState.deviceWidth || !globalState.deviceHeight || !globalState.isMouseDown) return;

    event.preventDefault();
    const coords = getScaledCoordinates(event);
    if (coords) {
        globalState.lastMousePosition = coords;
        sendMouseEvent(C.AMOTION_EVENT_ACTION_MOVE, globalState.currentMouseButtons, coords.x, coords.y);
    }
}

/**
 * 处理触摸结束事件
 * 修复了触摸结束时无法获取坐标导致的点击不松开问题
 */
function handleTouchEnd(event) {
    console.log('Touch end detected', event.type);
    if (!globalState.isMouseDown) return;
    if (!globalState.isRunning || !globalState.controlEnabledAtStart || !globalState.deviceWidth || !globalState.deviceHeight) {
        globalState.isMouseDown = false;
        globalState.currentMouseButtons = 0;
        return;
    }

    let buttonFlag = C.AMOTION_EVENT_BUTTON_PRIMARY;

    if (!(globalState.currentMouseButtons & buttonFlag)) return;

    // 获取坐标，优先使用当前事件坐标，否则使用上次有效坐标
    const coords = getScaledCoordinates(event) || globalState.lastMousePosition;
    
    // 坐标获取失败处理
    if (!coords) {
        console.error('Failed to get coordinates for touch end event');
        globalState.isMouseDown = false;
        globalState.currentMouseButtons = 0;
        return;
    }

    console.log('Sending touch up message', coords);
    sendMouseEvent(C.AMOTION_EVENT_ACTION_UP, globalState.currentMouseButtons, coords.x, coords.y);

    // 重置状态标志
    globalState.currentMouseButtons &= ~buttonFlag;
    globalState.isMouseDown = false;
    console.log('Mouse state reset:', globalState.isMouseDown, globalState.currentMouseButtons);
    
    // 延迟重置触摸处理标志，避免事件冲突
    setTimeout(() => {
        touchHandled = false;
    }, 300);
}

/**
 * 处理鼠标按下事件
 */
function handleMouseDown(event) {
    // 如果触摸事件已处理，则忽略鼠标事件
    if (touchHandled) {
        console.log('Ignoring mouse down due to touch event');
        return;
    }
    
    console.log('Mouse down detected');
    if (!globalState.isRunning || !globalState.controlEnabledAtStart || !globalState.deviceWidth || !globalState.deviceHeight) return;

    let activeRenderingElement;
    // 根据解码器类型选择活动渲染元素
    if (globalState.decoderType === C.DECODER_TYPES.BROADWAY) {
        activeRenderingElement = globalState.broadwayPlayer ? globalState.broadwayPlayer.canvas : elements.broadwayCanvas;
    } else if (globalState.decoderType === C.DECODER_TYPES.WEBCODECS) {
        activeRenderingElement = elements.webcodecCanvas;
    } else { // MSE or default
        activeRenderingElement = elements.videoElement;
    }

    // 检查事件是否发生在渲染元素内
    if (!activeRenderingElement || !event.target || (event.target !== activeRenderingElement && !activeRenderingElement.contains(event.target))) {
        if (event.target !== elements.streamArea) return;
    }

    event.preventDefault();
    globalState.isMouseDown = true;

    // 右键处理为返回键
    if (event.button === 2) {
        sendBackButtonControlInternal();
        globalState.isMouseDown = false;
        globalState.currentMouseButtons = 0;
        return;
    }

    // 根据鼠标按钮设置标志
    let buttonFlag = 0;
    switch (event.button) {
        case 0: buttonFlag = C.AMOTION_EVENT_BUTTON_PRIMARY; break;
        case 1: buttonFlag = C.AMOTION_EVENT_BUTTON_TERTIARY; break;
        default: return;
    }
    globalState.currentMouseButtons |= buttonFlag;
    const coords = getScaledCoordinates(event);
    if (coords) {
        globalState.lastMousePosition = coords;
        sendMouseEvent(C.AMOTION_EVENT_ACTION_DOWN, globalState.currentMouseButtons, coords.x, coords.y);
    }
}

/**
 * 处理鼠标释放事件
 */
function handleMouseUp(event) {
    // 如果触摸事件已处理，则忽略鼠标事件
    if (touchHandled) {
        console.log('Ignoring mouse up due to touch event');
        // 重置标志，允许下一次鼠标事件
        setTimeout(() => {
            touchHandled = false;
        }, 300);
        return;
    }
    
    console.log('Mouse up detected');
    if (!globalState.isMouseDown) return;
    if (!globalState.isRunning || !globalState.controlEnabledAtStart || !globalState.deviceWidth || !globalState.deviceHeight) {
        globalState.isMouseDown = false;
        globalState.currentMouseButtons = 0;
        return;
    }

    // 根据鼠标按钮获取标志
    let buttonFlag = 0;
    switch (event.button) {
        case 0: buttonFlag = C.AMOTION_EVENT_BUTTON_PRIMARY; break;
        case 1: buttonFlag = C.AMOTION_EVENT_BUTTON_TERTIARY; break;
        case 2: buttonFlag = C.AMOTION_EVENT_BUTTON_SECONDARY; break;
        default: return;
    }

    if (!(globalState.currentMouseButtons & buttonFlag)) return;

    // 获取坐标并发送鼠标释放事件
    const coords = getScaledCoordinates(event) || globalState.lastMousePosition;

    sendMouseEvent(C.AMOTION_EVENT_ACTION_UP, globalState.currentMouseButtons, coords.x, coords.y);
    globalState.currentMouseButtons &= ~buttonFlag;
    if (globalState.currentMouseButtons === 0) {
        globalState.isMouseDown = false;
    }
}

/**
 * 处理鼠标移动事件
 */
function handleMouseMove(event) {
    if (!globalState.isRunning || !globalState.controlEnabledAtStart || !globalState.deviceWidth || !globalState.deviceHeight || !globalState.isMouseDown) return;

    const coords = getScaledCoordinates(event);
    if (coords) {
        globalState.lastMousePosition = coords;
        sendMouseEvent(C.AMOTION_EVENT_ACTION_MOVE, globalState.currentMouseButtons, coords.x, coords.y);
    }
}

/**
 * 处理鼠标离开事件
 */
function handleMouseLeave(event) {
    if (!globalState.isRunning || !globalState.controlEnabledAtStart || !globalState.isMouseDown || globalState.currentMouseButtons === 0) return;
    
    const coords = getScaledCoordinates(event) || globalState.lastMousePosition;
    sendMouseEvent(C.AMOTION_EVENT_ACTION_UP, globalState.currentMouseButtons, coords.x, coords.y);
    globalState.isMouseDown = false;
    globalState.currentMouseButtons = 0;
}

/**
 * 处理滚轮事件
 */
function handleWheelEvent(event) {
    if (!globalState.isRunning || !globalState.controlEnabledAtStart || !globalState.deviceWidth || globalState.deviceWidth <= 0 || !globalState.deviceHeight || globalState.deviceHeight <= 0) return;
    
    let activeRenderingElement;
    // 根据解码器类型选择活动渲染元素
    if (globalState.decoderType === C.DECODER_TYPES.BROADWAY) {
        activeRenderingElement = globalState.broadwayPlayer ? globalState.broadwayPlayer.canvas : elements.broadwayCanvas;
    } else if (globalState.decoderType === C.DECODER_TYPES.WEBCODECS) {
        activeRenderingElement = elements.webcodecCanvas;
    } else { // MSE or default
        activeRenderingElement = elements.videoElement;
    }

    // 检查事件是否发生在渲染元素内
    if (!activeRenderingElement || !event.target || (event.target !== activeRenderingElement && !activeRenderingElement.contains(event.target))) {
       return;
    }

    // 阻止默认滚动行为
    event.preventDefault();

    // 获取坐标并计算滚动值
    let coords = getScaledCoordinates(event);
    if (!coords) {
        if (globalState.lastMousePosition.x > 0 || globalState.lastMousePosition.y > 0) {
            coords = globalState.lastMousePosition;
        } else {
            return;
        }
    }
    let hscroll_float = 0.0, vscroll_float = 0.0;
    const scrollSensitivity = 1.0;
    if (event.deltaX !== 0) {
        hscroll_float = event.deltaX > 0 ? -scrollSensitivity : scrollSensitivity;
    }
    if (event.deltaY !== 0) {
        vscroll_float = event.deltaY > 0 ? -scrollSensitivity : scrollSensitivity;
    }
    hscroll_float = Math.max(-1.0, Math.min(1.0, hscroll_float));
    vscroll_float = Math.max(-1.0, Math.min(1.0, vscroll_float));

    if (hscroll_float === 0.0 && vscroll_float === 0.0) return;

    // 转换为定点数并发送滚动消息
    const hscroll_fixed_point_short = Math.round(hscroll_float * 32767.0);
    const vscroll_fixed_point_short = Math.round(vscroll_float * 32767.0);

    const buffer = new ArrayBuffer(21);
    const dataView = new DataView(buffer);
    let offset = 0;
    dataView.setUint8(offset, C.CONTROL_MSG_TYPE_SCROLL_CLIENT); offset += 1;
    dataView.setInt32(offset, coords.x, false); offset += 4;
    dataView.setInt32(offset, coords.y, false); offset += 4;
    dataView.setUint16(offset, globalState.deviceWidth, false); offset += 2;
    dataView.setUint16(offset, globalState.deviceHeight, false); offset += 2;
    dataView.setInt16(offset, hscroll_fixed_point_short, false); offset += 2;
    dataView.setInt16(offset, vscroll_fixed_point_short, false); offset += 2;
    dataView.setInt32(offset, globalState.currentMouseButtons, false);
    sendControlMessageToServer(buffer);
}

/**
 * 初始化输入服务，注册所有事件监听器
 * 优化了事件绑定范围，只在streamArea元素上监听，避免全局事件干扰
 */
export function initInputService() {
    // 检查streamArea元素是否存在
    if (!elements.streamArea) {
        console.error('Stream area element not found, input service initialization failed');
        return;
    }

    // 触摸事件注册
    elements.streamArea.addEventListener('touchstart', handleTouchStart, { passive: false });
    elements.streamArea.addEventListener('touchmove', handleTouchMove, { passive: false });
    elements.streamArea.addEventListener('touchend', handleTouchEnd, { passive: false });
    
    // 鼠标事件注册
    elements.streamArea.addEventListener('mousedown', handleMouseDown);
    elements.streamArea.addEventListener('mousemove', handleMouseMove);
    elements.streamArea.addEventListener('mouseleave', handleMouseLeave);
    elements.streamArea.addEventListener('wheel', handleWheelEvent, { passive: false });
    
    // 鼠标释放事件需要在document上监听，确保不会丢失
    document.addEventListener('mouseup', handleMouseUp);
    
    // 右键菜单阻止逻辑
    elements.streamArea.addEventListener('contextmenu', (e) => {
        let activeRenderingElement;
        if (globalState.decoderType === C.DECODER_TYPES.BROADWAY) {
            activeRenderingElement = globalState.broadwayPlayer ? globalState.broadwayPlayer.canvas : elements.broadwayCanvas;
        } else if (globalState.decoderType === C.DECODER_TYPES.WEBCODECS) {
            activeRenderingElement = elements.webcodecCanvas;
        } else { // MSE or default
            activeRenderingElement = elements.videoElement;
        }
        
        // 仅在控制启用且事件发生在渲染元素内时阻止右键菜单
        if (globalState.controlEnabledAtStart && globalState.isRunning && activeRenderingElement && 
            (e.target === activeRenderingElement || activeRenderingElement.contains(e.target))) {
            e.preventDefault();
        }
    });
    
    console.log('Input service initialized successfully');
}